/**
 * <s-review-peek> — One-at-a-time review carousel in the product buy box.
 *
 * Required child markup:
 *   [data-peek-track]  — Horizontally scrolling list; each [data-peek-slide] fills its width.
 *   [data-peek-dot]    — Optional position dots; the one matching the visible card is highlighted.
 * Shoppers swipe the track by hand; there are no arrow buttons.
 */
class SReviewPeek extends HTMLElement {
  #ac;

  connectedCallback() {
    // Apps that copy the product form can clone this carousel; keep only the first copy on the page.
    const first = this.id ? document.querySelector(`s-review-peek[id="${CSS.escape(this.id)}"]`) : null;
    if (first && first !== this) {
      this.remove();
      return;
    }
    this.#ac?.abort();
    this.#ac = new AbortController();
    const options = { signal: this.#ac.signal };
    this.track = this.querySelector('[data-peek-track]');

    this.addEventListener('command', (event) => {
      if (event.command === '--peek-more') this.#toggleMore(event.source);
    }, options);

    // Show "Read more" only on cards whose text is cut off by the 3-line clamp.
    requestAnimationFrame(() => this.#syncMoreButtons());
    window.addEventListener('resize', () => this.#syncMoreButtons(), options);

    // After a swipe settles, collapse any expanded card that is no longer in view so the box shrinks back.
    let scrollTimer;
    this.track?.addEventListener('scroll', () => {
      this.#syncDots();
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => this.#collapseHidden(), 150);
    }, { ...options, passive: true });
  }

  #syncMoreButtons() {
    this.querySelectorAll('[data-peek-more]').forEach((button) => {
      if (button.getAttribute('aria-expanded') === 'true') return;
      const body = button.parentElement?.querySelector('[data-peek-body]');
      if (!body) return;
      // The button keeps its space either way so every collapsed card is the same height.
      const clipped = body.scrollHeight > body.clientHeight + 1;
      button.style.visibility = clipped ? 'visible' : 'hidden';
      button.tabIndex = clipped ? 0 : -1;
    });
  }

  #toggleMore(button, forceCollapse = false) {
    const body = button?.parentElement?.querySelector('[data-peek-body]');
    if (!body) return;
    const expand = forceCollapse ? false : button.getAttribute('aria-expanded') !== 'true';
    body.classList.toggle('line-clamp-3', !expand);
    button.setAttribute('aria-expanded', String(expand));
    button.textContent = expand ? button.dataset.lessLabel : button.dataset.moreLabel;
  }

  #collapseHidden() {
    if (!this.track) return;
    const width = this.track.clientWidth;
    if (!width) return;
    const current = Math.round(Math.abs(this.track.scrollLeft) / width);
    this.track.querySelectorAll('[data-peek-slide]').forEach((slide, index) => {
      if (index === current) return;
      const button = slide.querySelector('[data-peek-more][aria-expanded="true"]');
      if (button) this.#toggleMore(button, true);
    });
  }


  disconnectedCallback() {
    this.#ac?.abort();
  }

  #syncDots() {
    if (!this.track) return;
    const width = this.track.clientWidth;
    if (!width) return;
    const current = Math.round(Math.abs(this.track.scrollLeft) / width);
    this.querySelectorAll('[data-peek-dot]').forEach((dot, index) => {
      const active = index === current;
      dot.classList.toggle('w-4', active);
      dot.classList.toggle('bg-accent', active);
      dot.classList.toggle('w-1.5', !active);
      dot.classList.toggle('bg-accent/30', !active);
    });
  }
}

if (!customElements.get('s-review-peek')) customElements.define('s-review-peek', SReviewPeek);
